import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowDown, FiArrowRight, FiMap } from 'react-icons/fi';
import PageSeo from '../components/PageSeo';
import PropertyFilters from '../components/filtros/PropertyFilters';
import PropertyList from '../components/PropertyList';
import NotFound from './NotFound';

const LANDINGS = {
  '/propiedades/mendoza': {
    title: 'Propiedades en Mendoza | Casas y departamentos | NIDO',
    description: 'Buscá propiedades en Mendoza para comprar o alquilar. Explorá casas, departamentos, terrenos y desarrollos por ubicación en NIDO.',
    eyebrow: 'PORTAL INMOBILIARIO DE MENDOZA',
    heading: 'Propiedades en Mendoza',
    lead: 'Explorá casas, departamentos, terrenos y otros inmuebles publicados en Mendoza. Usá los filtros o el mapa para encontrar opciones por ciudad, departamento y zona.',
    filters: { province: 'mendoza' },
    questions: [
      ['¿Qué propiedades puedo buscar en Mendoza?', 'NIDO permite explorar casas, departamentos, terrenos, locales y otros tipos de inmuebles publicados en la provincia. Podés elegir compra o alquiler y ajustar la búsqueda según precio, superficie y características.'],
      ['¿Cómo busco por departamento, barrio o dirección?', 'Usá el campo de ubicación para escribir una ciudad, departamento, barrio o dirección. Las sugerencias te ayudan a seleccionar el lugar y los resultados se actualizan con esa ubicación.'],
      ['¿Cómo comparo propiedades desde el mapa?', 'Abrí “Ver mapa” en mobile o consultá el mapa junto al listado en desktop. Cada marcador corresponde a una publicación disponible y permite entender su ubicación antes de abrir la ficha.'],
      ['¿Cómo contacto a un anunciante en NIDO?', 'Entrá a la ficha de la propiedad y pulsá “Contactar” o “Coordinar una visita”. NIDO abre una conversación para que consultes disponibilidad y condiciones directamente con el anunciante.'],
    ],
    related: [
      ['/propiedades/ciudad-de-mendoza', 'Propiedades en Ciudad de Mendoza'],
      ['/propiedades/godoy-cruz', 'Propiedades en Godoy Cruz'],
      ['/propiedades/guaymallen', 'Propiedades en Guaymallén'],
      ['/propiedades/maipu', 'Propiedades en Maipú'],
      ['/propiedades/lujan-de-cuyo', 'Propiedades en Luján de Cuyo'],
      ['/propiedades/las-heras', 'Propiedades en Las Heras'],
      ['/alquileres/mendoza', 'Alquileres en Mendoza'],
      ['/casas-en-venta/mendoza', 'Casas en venta en Mendoza'],
    ],
  },
  '/propiedades/ciudad-de-mendoza': {
    title: 'Propiedades en Ciudad de Mendoza | Comprar o alquilar | NIDO',
    description: 'Buscá propiedades en Ciudad de Mendoza para comprar o alquilar. Explorá casas, departamentos y otros inmuebles por zona desde el mapa de NIDO.',
    eyebrow: 'PORTAL INMOBILIARIO DE CIUDAD DE MENDOZA',
    heading: 'Propiedades en Ciudad de Mendoza',
    lead: 'Explorá los inmuebles publicados en Mendoza Capital y compará opciones por barrio, operación, precio y características desde el listado y el mapa.',
    filters: { province: 'mendoza', department: 'Capital' },
    questions: [
      ['¿Cómo busco propiedades en Ciudad de Mendoza?', 'Esta página abre la búsqueda con Mendoza y Capital ya seleccionadas. Podés elegir compra o alquiler, indicar el tipo de inmueble y ajustar precio, superficie, ambientes y otras características.'],
      ['¿Qué barrios puedo explorar en Mendoza Capital?', 'Podés precisar la ubicación escribiendo barrios y zonas como Centro, Quinta Sección, Primera Sección, Bombal o Sexta Sección. Los resultados dependerán de las publicaciones disponibles en cada lugar.'],
      ['¿Cómo comparo las ubicaciones desde el mapa?', 'Consultá el mapa junto al listado en desktop o abrí “Ver mapa” en mobile. Así podés revisar la cercanía de cada publicación con parques, servicios, comercios y los sectores que te interesan.'],
      ['¿Puedo buscar tanto propiedades en venta como en alquiler?', 'Sí. La landing reúne las operaciones publicadas en Ciudad de Mendoza y permite cambiar entre compra y alquiler desde los filtros, sin salir de la búsqueda local.'],
    ],
    related: [
      ['/propiedades/mendoza', 'Todas las propiedades en Mendoza'],
      ['/alquileres/mendoza', 'Alquileres en Mendoza'],
      ['/zonas/mendoza/quinta-seccion', 'Guía de Quinta Sección'],
      ['/zonas/primera-seccion', 'Guía de Primera Sección'],
    ],
  },
  '/propiedades/godoy-cruz': {
    title: 'Propiedades en Godoy Cruz | Comprar o alquilar | NIDO',
    description: 'Buscá propiedades en Godoy Cruz, Mendoza, para comprar o alquilar. Compará casas, departamentos y otros inmuebles desde el mapa de NIDO.',
    eyebrow: 'PORTAL INMOBILIARIO DE GODOY CRUZ',
    heading: 'Propiedades en Godoy Cruz',
    lead: 'Explorá casas, departamentos y otros inmuebles publicados en Godoy Cruz. Ajustá la búsqueda por operación, barrio, precio y características.',
    filters: { province: 'mendoza', department: 'Godoy Cruz' },
    questions: [
      ['¿Cómo busco propiedades en Godoy Cruz?', 'La búsqueda abre Mendoza y Godoy Cruz ya seleccionados. Podés elegir venta o alquiler, definir el tipo de inmueble y ajustar precio, superficie, ambientes y características.'],
      ['¿Qué zonas de Godoy Cruz puedo explorar?', 'Podés precisar barrios y zonas como Centro, Bombal Sur, Benegas, Villa Hipódromo o Trapiche desde el campo de ubicación. Los resultados dependen de las publicaciones disponibles.'],
      ['¿Cómo comparo propiedades según su ubicación?', 'Usá el mapa para observar accesos, distancias y el entorno de cada publicación. En mobile podés abrirlo desde “Ver mapa” y volver al listado cuando quieras.'],
      ['¿Cómo contacto al anunciante?', 'Abrí la ficha de la propiedad y utilizá “Contactar” o “Coordinar una visita” para consultar disponibilidad, condiciones y cualquier dato que necesites confirmar.'],
    ],
    related: [
      ['/propiedades/mendoza', 'Todas las propiedades en Mendoza'],
      ['/propiedades/ciudad-de-mendoza', 'Propiedades en Ciudad de Mendoza'],
      ['/zonas/godoy-cruz', 'Guía para vivir en Godoy Cruz'],
    ],
  },
  '/propiedades/guaymallen': {
    title: 'Propiedades en Guaymallén | Comprar o alquilar | NIDO',
    description: 'Buscá propiedades en Guaymallén, Mendoza, para comprar o alquilar. Explorá casas, departamentos y otros inmuebles con filtros y mapa.',
    eyebrow: 'PORTAL INMOBILIARIO DE GUAYMALLÉN',
    heading: 'Propiedades en Guaymallén',
    lead: 'Encontrá inmuebles publicados en Guaymallén y compará alternativas por distrito, operación, precio, superficie y características desde NIDO.',
    filters: { province: 'mendoza', department: 'Guaymallén' },
    questions: [
      ['¿Cómo busco propiedades en Guaymallén?', 'Esta página mantiene Mendoza y Guaymallén como ubicación inicial. Después podés elegir compra o alquiler, tipo de propiedad, rango de precio y características.'],
      ['¿Puedo buscar por distrito o barrio?', 'Sí. Escribí en ubicación zonas como Dorrego, San José, Villa Nueva, Buena Nueva o Rodeo de la Cruz para precisar la búsqueda según la oferta publicada.'],
      ['¿Qué información puedo comparar?', 'Cada ficha puede incluir precio y moneda, superficies, ambientes, dormitorios, estado, servicios, imágenes y ubicación. El mapa permite comparar el entorno antes de contactar.'],
      ['¿Cómo guardo una propiedad para verla después?', 'Desde la tarjeta o la ficha podés marcarla como favorita. Si cambia su disponibilidad, NIDO conserva la referencia y te permite explorar alternativas similares.'],
    ],
    related: [
      ['/propiedades/mendoza', 'Todas las propiedades en Mendoza'],
      ['/propiedades/ciudad-de-mendoza', 'Propiedades en Ciudad de Mendoza'],
      ['/propiedades/godoy-cruz', 'Propiedades en Godoy Cruz'],
      ['/alquileres/mendoza', 'Alquileres en Mendoza'],
    ],
  },
  '/propiedades/maipu': {
    title: 'Propiedades en Maipú, Mendoza | Comprar o alquilar | NIDO',
    description: 'Buscá propiedades en Maipú, Mendoza, para comprar o alquilar. Compará casas, terrenos, departamentos y otros inmuebles en NIDO.',
    eyebrow: 'PORTAL INMOBILIARIO DE MAIPÚ',
    heading: 'Propiedades en Maipú, Mendoza',
    lead: 'Explorá la oferta inmobiliaria publicada en Maipú y filtrá por operación, zona, precio, superficie y las características que necesitás.',
    filters: { province: 'mendoza', department: 'Maipú' },
    questions: [
      ['¿Cómo busco propiedades en Maipú?', 'La landing carga Mendoza y Maipú como filtros iniciales. Podés sumar venta o alquiler, elegir casa, departamento, terreno u otro tipo y ajustar el resto de los criterios.'],
      ['¿Puedo buscar en zonas específicas de Maipú?', 'Sí. Usá el campo de ubicación para explorar Maipú centro, Coquimbito, Luzuriaga, Gutiérrez, Russell u otros distritos presentes en las publicaciones.'],
      ['¿Cómo encuentro casas con jardín o pileta?', 'Abrí “Más filtros” y activá jardín, pileta, cochera, quincho u otras características. NIDO mostrará las publicaciones que hayan informado esas prestaciones.'],
      ['¿El mapa muestra la ubicación de cada propiedad?', 'Las publicaciones con coordenadas aparecen en el mapa. Esto permite comparar accesos y zonas; la dirección y disponibilidad deben confirmarse con el anunciante.'],
    ],
    related: [
      ['/propiedades/mendoza', 'Todas las propiedades en Mendoza'],
      ['/propiedades/lujan-de-cuyo', 'Propiedades en Luján de Cuyo'],
      ['/propiedades/guaymallen', 'Propiedades en Guaymallén'],
      ['/casas-en-venta/mendoza', 'Casas en venta en Mendoza'],
    ],
  },
  '/propiedades/lujan-de-cuyo': {
    title: 'Propiedades en Luján de Cuyo | Comprar o alquilar | NIDO',
    description: 'Buscá propiedades en Luján de Cuyo, Mendoza. Explorá casas, departamentos, terrenos y otros inmuebles en venta o alquiler con NIDO.',
    eyebrow: 'PORTAL INMOBILIARIO DE LUJÁN DE CUYO',
    heading: 'Propiedades en Luján de Cuyo',
    lead: 'Explorá inmuebles publicados en Luján de Cuyo y compará alternativas urbanas, residenciales y de montaña desde los filtros y el mapa.',
    filters: { province: 'mendoza', department: 'Luján de Cuyo' },
    questions: [
      ['¿Cómo busco propiedades en Luján de Cuyo?', 'Esta página abre la búsqueda con Mendoza y Luján de Cuyo seleccionados. Podés definir operación, tipo de inmueble, precio, superficie y prestaciones.'],
      ['¿Qué zonas puedo explorar?', 'Podés escribir Chacras de Coria, Vistalba, Mayor Drummond, Carrodilla, Cacheuta, Potrerillos u otra localidad para ajustar los resultados disponibles.'],
      ['¿Cómo comparo casas, departamentos y terrenos?', 'Elegí el tipo de propiedad desde los filtros y revisá precio, superficie, características y ubicación. También podés quitar el filtro para volver a ver toda la oferta del departamento.'],
      ['¿Qué debo confirmar antes de coordinar una visita?', 'Consultá al anunciante la disponibilidad, dirección, condiciones de la operación, servicios y documentación. NIDO facilita el contacto, pero esos datos deben verificarse directamente.'],
    ],
    related: [
      ['/propiedades/mendoza', 'Todas las propiedades en Mendoza'],
      ['/zonas/chacras-de-coria', 'Guía de Chacras de Coria'],
      ['/propiedades/maipu', 'Propiedades en Maipú'],
      ['/casas-en-venta/mendoza', 'Casas en venta en Mendoza'],
    ],
  },
  '/propiedades/las-heras': {
    title: 'Propiedades en Las Heras, Mendoza | Comprar o alquilar | NIDO',
    description: 'Buscá propiedades en Las Heras, Mendoza, para comprar o alquilar. Explorá casas, terrenos, departamentos y otros inmuebles en NIDO.',
    eyebrow: 'PORTAL INMOBILIARIO DE LAS HERAS',
    heading: 'Propiedades en Las Heras, Mendoza',
    lead: 'Consultá los inmuebles publicados en Las Heras y recorré opciones por operación, localidad, precio, superficie y características desde el mapa.',
    filters: { province: 'mendoza', department: 'Las Heras' },
    questions: [
      ['¿Cómo busco propiedades en Las Heras?', 'La búsqueda comienza con Mendoza y Las Heras seleccionados. Después podés elegir compra o alquiler, tipo de inmueble, precio, superficie y otros filtros.'],
      ['¿Puedo buscar por localidad o zona?', 'Sí. Indicá El Challao, Panquehua, El Algarrobal, Uspallata u otra zona en el campo de ubicación para precisar las publicaciones que querés explorar.'],
      ['¿Cómo uso el mapa en el teléfono?', 'Pulsá “Ver mapa” para abrir la vista geográfica de los resultados y volvé al listado cuando necesites revisar fotos, precios y detalles de cada propiedad.'],
      ['¿Cómo recibo más información de una publicación?', 'Entrá en la ficha y contactá al anunciante desde los botones disponibles. Podrás consultar condiciones, documentación, servicios y coordinar una visita.'],
    ],
    related: [
      ['/propiedades/mendoza', 'Todas las propiedades en Mendoza'],
      ['/propiedades/ciudad-de-mendoza', 'Propiedades en Ciudad de Mendoza'],
      ['/propiedades/lujan-de-cuyo', 'Propiedades en Luján de Cuyo'],
      ['/alquileres/mendoza', 'Alquileres en Mendoza'],
    ],
  },
  '/propiedades/san-juan': {
    title: 'Propiedades en San Juan | Portal inmobiliario NIDO',
    description: 'Encontrá propiedades en San Juan para comprar o alquilar. Buscá casas, departamentos, terrenos y otros inmuebles en NIDO.',
    eyebrow: 'PORTAL INMOBILIARIO DE SAN JUAN',
    heading: 'Propiedades en San Juan',
    lead: 'Reunimos la oferta publicada en San Juan para que puedas explorarla por operación, tipo de propiedad y ubicación desde una sola búsqueda.',
    filters: { province: 'san_juan' },
    related: [
      ['/alquileres/san-juan', 'Alquileres en San Juan'],
      ['/zonas/rivadavia-san-juan', 'Vivir en Rivadavia'],
      ['/vivir-cuyo/montana-y-aventura', 'Conocer San Juan'],
      ['/explorar-cuyo', 'Explorar Cuyo'],
    ],
  },
  '/propiedades/san-luis': {
    title: 'Propiedades en San Luis | Portal inmobiliario NIDO',
    description: 'Buscá propiedades en San Luis para comprar o alquilar. Explorá casas, departamentos, terrenos y otros inmuebles en NIDO.',
    eyebrow: 'PORTAL INMOBILIARIO DE SAN LUIS',
    heading: 'Propiedades en San Luis',
    lead: 'Explorá los inmuebles publicados en San Luis y ajustá la búsqueda por operación, tipo y ubicación. También podés recorrer los resultados desde el mapa.',
    filters: { province: 'san_luis' },
    related: [
      ['/casas-en-venta/san-luis', 'Casas en venta en San Luis'],
      ['/zonas/potrero-de-los-funes', 'Vivir en Potrero de los Funes'],
      ['/zonas/el-trapiche-san-luis', 'Vivir en El Trapiche'],
      ['/vivir-cuyo/sierras-y-naturaleza', 'Conocer San Luis'],
    ],
  },
  '/alquileres/mendoza': {
    title: 'Alquileres en Mendoza | Casas y departamentos | NIDO',
    description: 'Buscá alquileres en Mendoza. Explorá casas, departamentos y otros inmuebles disponibles con filtros y mapa en NIDO.',
    eyebrow: 'ALQUILERES EN MENDOZA',
    heading: 'Alquileres en Mendoza',
    lead: 'Encontrá casas, departamentos y otros inmuebles ofrecidos en alquiler en Mendoza. Compará ubicaciones y recorré los resultados desde el mapa.',
    filters: { province: 'mendoza', operation: 'alquiler' },
    questions: [
      ['¿Cómo encuentro alquileres en Mendoza?', 'Esta página abre la búsqueda con Mendoza y la operación alquiler ya seleccionadas. Podés sumar una ubicación más precisa, elegir el tipo de inmueble y ordenar los resultados.'],
      ['¿Qué filtros puedo utilizar?', 'Podés ajustar precio, ambientes, dormitorios, baños, superficie, moneda y estado. También hay filtros para cochera, mascotas, pileta, jardín, quincho, terraza y otras características.'],
      ['¿Cómo busco propiedades que acepten mascotas?', 'Abrí “Más filtros” y activá “Acepta mascotas”. La búsqueda conservará Mendoza y alquiler como criterios principales y mostrará las publicaciones que indiquen esa condición.'],
      ['¿Cómo consulto condiciones o coordino una visita?', 'Abrí la publicación que te interesa y utilizá sus botones de contacto. El contrato, la disponibilidad, los requisitos y cualquier pago deben confirmarse directamente con el anunciante.'],
    ],
    related: [
      ['/alquileres/ciudad-de-mendoza', 'Alquileres en Ciudad de Mendoza'],
      ['/alquileres/godoy-cruz', 'Alquileres en Godoy Cruz'],
      ['/propiedades/mendoza', 'Todas las propiedades en Mendoza'],
      ['/casas-en-venta/mendoza', 'Casas en venta en Mendoza'],
      ['/zonas/godoy-cruz', 'Guía de Godoy Cruz'],
    ],
  },
  '/alquileres/ciudad-de-mendoza': {
    title: 'Alquileres en Ciudad de Mendoza | Casas y departamentos | NIDO',
    description: 'Buscá alquileres en Ciudad de Mendoza. Compará casas, departamentos y otros inmuebles disponibles por barrio, precio y características.',
    eyebrow: 'ALQUILERES EN MENDOZA CAPITAL',
    heading: 'Alquileres en Ciudad de Mendoza',
    lead: 'Explorá casas, departamentos y otros inmuebles publicados en alquiler en Mendoza Capital. Compará barrios, precios y ubicaciones desde NIDO.',
    filters: { province: 'mendoza', department: 'Capital', operation: 'alquiler' },
    questions: [
      ['¿Cómo encuentro alquileres en Ciudad de Mendoza?', 'La búsqueda carga Mendoza, Capital y alquiler como criterios iniciales. Podés sumar tipo de inmueble, barrio, precio, ambientes, superficie y otras preferencias.'],
      ['¿Puedo buscar departamentos por barrio?', 'Sí. Escribí Centro, Quinta Sección, Primera Sección, Bombal, Sexta Sección u otra zona para consultar las publicaciones disponibles en ese sector.'],
      ['¿Cómo encuentro alquileres que acepten mascotas?', 'Abrí “Más filtros” y activá “Acepta mascotas”. Los resultados mostrarán las publicaciones que hayan indicado expresamente esa condición.'],
      ['¿Qué condiciones debo consultar antes de alquilar?', 'Confirmá con el anunciante disponibilidad, plazo, actualización, depósito, garantías, expensas, servicios y cualquier gasto adicional antes de avanzar.'],
    ],
    related: [
      ['/propiedades/ciudad-de-mendoza', 'Propiedades en Ciudad de Mendoza'],
      ['/alquileres/mendoza', 'Todos los alquileres en Mendoza'],
      ['/casas-en-venta/ciudad-de-mendoza', 'Casas en venta en Ciudad de Mendoza'],
      ['/zonas/mendoza/quinta-seccion', 'Guía de Quinta Sección'],
    ],
  },
  '/alquileres/godoy-cruz': {
    title: 'Alquileres en Godoy Cruz | Casas y departamentos | NIDO',
    description: 'Buscá alquileres en Godoy Cruz, Mendoza. Explorá casas, departamentos y otros inmuebles con filtros de precio, zona y características.',
    eyebrow: 'ALQUILERES EN GODOY CRUZ',
    heading: 'Alquileres en Godoy Cruz',
    lead: 'Encontrá inmuebles publicados en alquiler en Godoy Cruz y recorré alternativas por barrio, tipo, precio y características desde el listado y el mapa.',
    filters: { province: 'mendoza', department: 'Godoy Cruz', operation: 'alquiler' },
    questions: [
      ['¿Cómo busco alquileres en Godoy Cruz?', 'Esta landing abre Mendoza, Godoy Cruz y alquiler seleccionados. Podés elegir casa o departamento y ajustar precio, ambientes, superficie y prestaciones.'],
      ['¿Puedo precisar una zona dentro de Godoy Cruz?', 'Sí. Usá ubicación para buscar Centro, Benegas, Bombal Sur, Trapiche, Villa Hipódromo u otra zona incluida en las publicaciones.'],
      ['¿Cómo comparo el costo de distintas propiedades?', 'Revisá precio, moneda, expensas informadas, superficie y características de cada ficha. Consultá directamente cualquier gasto que no esté detallado.'],
      ['¿Cómo coordino una visita?', 'Abrí la publicación y pulsá “Contactar” o “Coordinar una visita”. La conversación te permite consultar disponibilidad y acordar los detalles con el anunciante.'],
    ],
    related: [
      ['/propiedades/godoy-cruz', 'Propiedades en Godoy Cruz'],
      ['/alquileres/mendoza', 'Todos los alquileres en Mendoza'],
      ['/casas-en-venta/godoy-cruz', 'Casas en venta en Godoy Cruz'],
      ['/zonas/godoy-cruz', 'Guía para vivir en Godoy Cruz'],
    ],
  },
  '/alquileres/guaymallen': {
    title: 'Alquileres en Guaymallén | Casas y departamentos | NIDO',
    description: 'Buscá alquileres en Guaymallén, Mendoza. Compará casas, departamentos y otros inmuebles por distrito, precio y características.',
    eyebrow: 'ALQUILERES EN GUAYMALLÉN',
    heading: 'Alquileres en Guaymallén',
    lead: 'Explorá inmuebles publicados en alquiler en Guaymallén y compará opciones por distrito, tipo, precio, superficie y prestaciones.',
    filters: { province: 'mendoza', department: 'Guaymallén', operation: 'alquiler' },
    questions: [
      ['¿Cómo busco alquileres en Guaymallén?', 'La página abre Mendoza, Guaymallén y alquiler seleccionados. Podés elegir tipo de inmueble, distrito, precio, ambientes, superficie y características.'],
      ['¿Puedo buscar por distrito?', 'Sí. Escribí Dorrego, San José, Villa Nueva, Buena Nueva, Rodeo de la Cruz u otra zona para precisar la oferta publicada.'],
      ['¿Qué debo confirmar antes de alquilar?', 'Consultá disponibilidad, plazo, actualización, depósito, garantías, expensas, servicios y gastos adicionales directamente con el anunciante.'],
    ],
    related: [
      ['/propiedades/guaymallen', 'Propiedades en Guaymallén'],
      ['/alquileres/mendoza', 'Todos los alquileres en Mendoza'],
      ['/casas-en-venta/guaymallen', 'Casas en venta en Guaymallén'],
      ['/alquileres/ciudad-de-mendoza', 'Alquileres en Ciudad de Mendoza'],
    ],
  },
  '/alquileres/maipu': {
    title: 'Alquileres en Maipú, Mendoza | Casas y departamentos | NIDO',
    description: 'Buscá alquileres en Maipú, Mendoza. Explorá casas, departamentos y otros inmuebles por localidad, precio y características.',
    eyebrow: 'ALQUILERES EN MAIPÚ',
    heading: 'Alquileres en Maipú, Mendoza',
    lead: 'Encontrá propiedades publicadas en alquiler en Maipú y ajustá la búsqueda por localidad, tipo, precio, superficie y prestaciones.',
    filters: { province: 'mendoza', department: 'Maipú', operation: 'alquiler' },
    questions: [
      ['¿Cómo encuentro alquileres en Maipú?', 'Esta búsqueda conserva Mendoza, Maipú y alquiler. Después podés seleccionar casa, departamento u otro tipo y ajustar precio, ambientes y superficie.'],
      ['¿Qué localidades puedo buscar?', 'Usá ubicación para explorar Maipú centro, Luzuriaga, Gutiérrez, Coquimbito, Russell u otras localidades presentes en las publicaciones.'],
      ['¿Cómo coordino una visita?', 'Abrí la ficha y utilizá sus botones de contacto para consultar condiciones, confirmar disponibilidad y acordar una visita con el anunciante.'],
    ],
    related: [
      ['/propiedades/maipu', 'Propiedades en Maipú'],
      ['/alquileres/mendoza', 'Todos los alquileres en Mendoza'],
      ['/casas-en-venta/maipu', 'Casas en venta en Maipú'],
      ['/alquileres/lujan-de-cuyo', 'Alquileres en Luján de Cuyo'],
    ],
  },
  '/alquileres/lujan-de-cuyo': {
    title: 'Alquileres en Luján de Cuyo | Casas y departamentos | NIDO',
    description: 'Buscá alquileres en Luján de Cuyo, Mendoza. Compará casas, departamentos y otros inmuebles por localidad y características.',
    eyebrow: 'ALQUILERES EN LUJÁN DE CUYO',
    heading: 'Alquileres en Luján de Cuyo',
    lead: 'Explorá propiedades en alquiler en Luján de Cuyo y compará opciones por localidad, entorno, precio, superficie y prestaciones.',
    filters: { province: 'mendoza', department: 'Luján de Cuyo', operation: 'alquiler' },
    questions: [
      ['¿Cómo busco alquileres en Luján de Cuyo?', 'La landing abre Mendoza, Luján de Cuyo y alquiler seleccionados. Podés agregar tipo de propiedad, localidad, precio y características.'],
      ['¿Puedo buscar en Chacras de Coria o Vistalba?', 'Sí. Escribí Chacras de Coria, Vistalba, Mayor Drummond, Carrodilla u otra localidad para consultar los avisos disponibles allí.'],
      ['¿Cómo comparo el entorno de cada propiedad?', 'Usá el mapa para revisar accesos, distancias y ubicación. Los servicios, condiciones y dirección deben confirmarse en la ficha y con el anunciante.'],
    ],
    related: [
      ['/propiedades/lujan-de-cuyo', 'Propiedades en Luján de Cuyo'],
      ['/alquileres/mendoza', 'Todos los alquileres en Mendoza'],
      ['/casas-en-venta/lujan-de-cuyo', 'Casas en venta en Luján de Cuyo'],
      ['/zonas/chacras-de-coria', 'Guía de Chacras de Coria'],
    ],
  },
  '/alquileres/las-heras': {
    title: 'Alquileres en Las Heras, Mendoza | Casas y departamentos | NIDO',
    description: 'Buscá alquileres en Las Heras, Mendoza. Explorá casas, departamentos y otros inmuebles por zona, precio y características.',
    eyebrow: 'ALQUILERES EN LAS HERAS',
    heading: 'Alquileres en Las Heras, Mendoza',
    lead: 'Consultá propiedades publicadas en alquiler en Las Heras y compará opciones por localidad, tipo, precio, superficie y características.',
    filters: { province: 'mendoza', department: 'Las Heras', operation: 'alquiler' },
    questions: [
      ['¿Cómo busco alquileres en Las Heras?', 'La búsqueda carga Mendoza, Las Heras y alquiler como filtros iniciales. Podés elegir tipo, zona, precio, superficie y prestaciones.'],
      ['¿Puedo precisar una localidad?', 'Sí. Indicá El Challao, Panquehua, El Algarrobal, Uspallata u otra zona para ajustar los resultados publicados.'],
      ['¿Cómo contacto al anunciante?', 'Entrá en la ficha y pulsá “Contactar” o “Coordinar una visita” para confirmar disponibilidad, requisitos y condiciones del alquiler.'],
    ],
    related: [
      ['/propiedades/las-heras', 'Propiedades en Las Heras'],
      ['/alquileres/mendoza', 'Todos los alquileres en Mendoza'],
      ['/casas-en-venta/las-heras', 'Casas en venta en Las Heras'],
      ['/alquileres/ciudad-de-mendoza', 'Alquileres en Ciudad de Mendoza'],
    ],
  },
  '/alquileres/san-juan': {
    title: 'Alquileres en San Juan | Casas y departamentos | NIDO',
    description: 'Buscá alquileres en San Juan. Explorá casas, departamentos y otros inmuebles publicados con filtros y mapa en NIDO.',
    eyebrow: 'ALQUILERES EN SAN JUAN',
    heading: 'Alquileres en San Juan',
    lead: 'Consultá los inmuebles ofrecidos en alquiler en San Juan. Usá los filtros para ajustar el tipo, la ubicación, el precio y las características que necesitás.',
    filters: { province: 'san_juan', operation: 'alquiler' },
    related: [
      ['/propiedades/san-juan', 'Todas las propiedades en San Juan'],
      ['/zonas/rivadavia-san-juan', 'Guía de Rivadavia'],
      ['/vivir-cuyo/montana-y-aventura', 'Conocer San Juan'],
      ['/explorar-cuyo', 'Explorar Cuyo'],
    ],
  },
  '/casas-en-venta/mendoza': {
    title: 'Casas en venta en Mendoza | Comprar casa | NIDO',
    description: 'Buscá casas en venta en Mendoza. Filtrá por ubicación, precio y características, y explorá las propiedades en el mapa de NIDO.',
    eyebrow: 'COMPRAR CASA EN MENDOZA',
    heading: 'Casas en venta en Mendoza',
    lead: 'Explorá las casas publicadas en venta en Mendoza. Ajustá la búsqueda por ubicación, precio, superficie y características para encontrar opciones relevantes.',
    filters: { province: 'mendoza', operation: 'venta', type: 'casa' },
    questions: [
      ['¿Cómo busco casas en venta en Mendoza?', 'La página carga Mendoza, venta y casa como filtros iniciales. Después podés indicar una zona, un rango de precio, superficie, dormitorios y otras preferencias.'],
      ['¿Cómo encuentro casas con jardín, cochera o pileta?', 'Abrí “Más filtros” y seleccioná las características que necesitás. Los resultados se ajustan para mostrar únicamente las casas cuyas publicaciones incluyen esos datos.'],
      ['¿Cómo comparo distintas zonas?', 'Usá el campo de ubicación para cambiar de departamento o barrio y consultá el mapa para comparar dónde se encuentra cada publicación disponible.'],
      ['¿Qué conviene revisar antes de contactar?', 'Revisá precio y moneda, ubicación, superficies, estado, ambientes, características, imágenes y referencia de la publicación. Si algo no está detallado, consultalo al anunciante desde la ficha.'],
    ],
    related: [
      ['/casas-en-venta/ciudad-de-mendoza', 'Casas en venta en Ciudad de Mendoza'],
      ['/casas-en-venta/godoy-cruz', 'Casas en venta en Godoy Cruz'],
      ['/casas-en-venta/lujan-de-cuyo', 'Casas en venta en Luján de Cuyo'],
      ['/propiedades/mendoza', 'Todas las propiedades en Mendoza'],
      ['/alquileres/mendoza', 'Alquileres en Mendoza'],
    ],
  },
  '/casas-en-venta/ciudad-de-mendoza': {
    title: 'Casas en venta en Ciudad de Mendoza | Comprar casa | NIDO',
    description: 'Buscá casas en venta en Ciudad de Mendoza. Compará propiedades por barrio, precio, superficie y características desde el mapa de NIDO.',
    eyebrow: 'COMPRAR CASA EN MENDOZA CAPITAL',
    heading: 'Casas en venta en Ciudad de Mendoza',
    lead: 'Explorá casas publicadas en venta en Mendoza Capital y compará alternativas por barrio, precio, superficie, ambientes y prestaciones.',
    filters: { province: 'mendoza', department: 'Capital', operation: 'venta', type: 'casa' },
    questions: [
      ['¿Cómo busco casas en venta en Ciudad de Mendoza?', 'La página carga Mendoza, Capital, venta y casa como filtros iniciales. Podés precisar barrio, precio, superficie, dormitorios y características.'],
      ['¿Qué zonas de Mendoza Capital puedo comparar?', 'Usá ubicación para explorar Centro, Quinta Sección, Primera Sección, Bombal, Sexta Sección u otros barrios con publicaciones disponibles.'],
      ['¿Cómo encuentro casas con cochera, jardín o pileta?', 'Abrí “Más filtros” y seleccioná las prestaciones necesarias. Los resultados se ajustarán según la información declarada en cada publicación.'],
      ['¿Qué información conviene verificar antes de comprar?', 'Además de precio, superficie y estado, consultá documentación, servicios, antigüedad, gastos, posibilidades de crédito y condiciones de la operación con el anunciante.'],
    ],
    related: [
      ['/propiedades/ciudad-de-mendoza', 'Propiedades en Ciudad de Mendoza'],
      ['/casas-en-venta/mendoza', 'Casas en venta en Mendoza'],
      ['/alquileres/ciudad-de-mendoza', 'Alquileres en Ciudad de Mendoza'],
      ['/zonas/mendoza/quinta-seccion', 'Guía de Quinta Sección'],
    ],
  },
  '/casas-en-venta/godoy-cruz': {
    title: 'Casas en venta en Godoy Cruz | Comprar casa | NIDO',
    description: 'Buscá casas en venta en Godoy Cruz, Mendoza. Compará opciones por barrio, precio, superficie y características en el mapa de NIDO.',
    eyebrow: 'COMPRAR CASA EN GODOY CRUZ',
    heading: 'Casas en venta en Godoy Cruz',
    lead: 'Consultá casas publicadas en venta en Godoy Cruz y compará ubicaciones, precios, superficies y características desde una búsqueda local.',
    filters: { province: 'mendoza', department: 'Godoy Cruz', operation: 'venta', type: 'casa' },
    questions: [
      ['¿Cómo encuentro casas en venta en Godoy Cruz?', 'La búsqueda mantiene Mendoza, Godoy Cruz, venta y casa seleccionados. Podés ajustar zona, precio, superficie, dormitorios, baños y prestaciones.'],
      ['¿Puedo buscar casas por barrio?', 'Sí. Escribí Benegas, Bombal Sur, Trapiche, Centro, Villa Hipódromo u otra zona para consultar la oferta publicada allí.'],
      ['¿Cómo comparo propiedades desde el mapa?', 'El mapa ayuda a revisar la ubicación, los accesos y las distancias entre publicaciones. Después podés abrir cada ficha para comparar fotos y características.'],
      ['¿Cómo contacto al propietario o profesional?', 'Entrá en la ficha y utilizá los botones de contacto. Consultá disponibilidad, documentación y condiciones antes de coordinar una visita o asumir compromisos.'],
    ],
    related: [
      ['/propiedades/godoy-cruz', 'Propiedades en Godoy Cruz'],
      ['/casas-en-venta/mendoza', 'Casas en venta en Mendoza'],
      ['/alquileres/godoy-cruz', 'Alquileres en Godoy Cruz'],
    ],
  },
  '/casas-en-venta/lujan-de-cuyo': {
    title: 'Casas en venta en Luján de Cuyo | Comprar casa | NIDO',
    description: 'Buscá casas en venta en Luján de Cuyo, Mendoza. Explorá opciones por localidad, precio, superficie y características desde NIDO.',
    eyebrow: 'COMPRAR CASA EN LUJÁN DE CUYO',
    heading: 'Casas en venta en Luján de Cuyo',
    lead: 'Explorá casas publicadas en venta en Luján de Cuyo y compará alternativas por localidad, entorno, precio, superficie y prestaciones.',
    filters: { province: 'mendoza', department: 'Luján de Cuyo', operation: 'venta', type: 'casa' },
    questions: [
      ['¿Cómo busco casas en venta en Luján de Cuyo?', 'La página carga Mendoza, Luján de Cuyo, venta y casa como selección inicial. Podés sumar localidad, precio, superficie y características.'],
      ['¿Qué localidades puedo explorar?', 'Buscá Chacras de Coria, Vistalba, Mayor Drummond, Carrodilla, Cacheuta, Potrerillos u otras localidades presentes en la oferta publicada.'],
      ['¿Cómo encuentro casas con jardín, pileta o quincho?', 'Seleccioná esas prestaciones en “Más filtros”. NIDO conservará la ubicación y mostrará únicamente las publicaciones que indiquen esas características.'],
      ['¿Qué aspectos de la ubicación conviene revisar?', 'Compará accesos, servicios, distancias y entorno desde el mapa. Para zonas extensas o de montaña también conviene confirmar caminos, servicios y conectividad con el anunciante.'],
    ],
    related: [
      ['/propiedades/lujan-de-cuyo', 'Propiedades en Luján de Cuyo'],
      ['/casas-en-venta/mendoza', 'Casas en venta en Mendoza'],
      ['/zonas/chacras-de-coria', 'Guía de Chacras de Coria'],
      ['/propiedades/maipu', 'Propiedades en Maipú'],
    ],
  },
  '/casas-en-venta/guaymallen': {
    title: 'Casas en venta en Guaymallén | Comprar casa | NIDO',
    description: 'Buscá casas en venta en Guaymallén, Mendoza. Compará propiedades por distrito, precio, superficie y características en NIDO.',
    eyebrow: 'COMPRAR CASA EN GUAYMALLÉN',
    heading: 'Casas en venta en Guaymallén',
    lead: 'Explorá casas publicadas en venta en Guaymallén y compará opciones por distrito, precio, superficie, ambientes y prestaciones.',
    filters: { province: 'mendoza', department: 'Guaymallén', operation: 'venta', type: 'casa' },
    questions: [
      ['¿Cómo busco casas en venta en Guaymallén?', 'La página mantiene Mendoza, Guaymallén, venta y casa seleccionados. Podés sumar distrito, precio, superficie, dormitorios y características.'],
      ['¿Qué distritos puedo explorar?', 'Buscá Dorrego, San José, Villa Nueva, Buena Nueva, Rodeo de la Cruz u otra zona según las publicaciones disponibles.'],
      ['¿Qué conviene verificar antes de comprar?', 'Consultá documentación, estado, servicios, antigüedad, gastos y condiciones de la operación directamente con el anunciante antes de avanzar.'],
    ],
    related: [
      ['/propiedades/guaymallen', 'Propiedades en Guaymallén'],
      ['/casas-en-venta/mendoza', 'Casas en venta en Mendoza'],
      ['/alquileres/guaymallen', 'Alquileres en Guaymallén'],
      ['/casas-en-venta/godoy-cruz', 'Casas en venta en Godoy Cruz'],
    ],
  },
  '/casas-en-venta/maipu': {
    title: 'Casas en venta en Maipú, Mendoza | Comprar casa | NIDO',
    description: 'Buscá casas en venta en Maipú, Mendoza. Explorá propiedades por localidad, precio, superficie y características desde NIDO.',
    eyebrow: 'COMPRAR CASA EN MAIPÚ',
    heading: 'Casas en venta en Maipú, Mendoza',
    lead: 'Compará casas publicadas en venta en Maipú por localidad, entorno, precio, superficie y prestaciones desde el listado y el mapa.',
    filters: { province: 'mendoza', department: 'Maipú', operation: 'venta', type: 'casa' },
    questions: [
      ['¿Cómo encuentro casas en venta en Maipú?', 'La búsqueda abre Mendoza, Maipú, venta y casa seleccionados. Podés ajustar localidad, precio, superficie, dormitorios y prestaciones.'],
      ['¿Puedo buscar casas con jardín o pileta?', 'Sí. Abrí “Más filtros” y elegí jardín, pileta, cochera, quincho u otras características informadas por las publicaciones.'],
      ['¿Cómo comparo distintas localidades?', 'Usá ubicación y el mapa para explorar Maipú centro, Luzuriaga, Gutiérrez, Coquimbito, Russell y otras zonas disponibles.'],
    ],
    related: [
      ['/propiedades/maipu', 'Propiedades en Maipú'],
      ['/casas-en-venta/mendoza', 'Casas en venta en Mendoza'],
      ['/alquileres/maipu', 'Alquileres en Maipú'],
      ['/casas-en-venta/lujan-de-cuyo', 'Casas en venta en Luján de Cuyo'],
    ],
  },
  '/casas-en-venta/las-heras': {
    title: 'Casas en venta en Las Heras, Mendoza | Comprar casa | NIDO',
    description: 'Buscá casas en venta en Las Heras, Mendoza. Compará propiedades por localidad, precio, superficie y características desde NIDO.',
    eyebrow: 'COMPRAR CASA EN LAS HERAS',
    heading: 'Casas en venta en Las Heras, Mendoza',
    lead: 'Explorá casas publicadas en venta en Las Heras y compará alternativas por localidad, precio, superficie, ambientes y prestaciones.',
    filters: { province: 'mendoza', department: 'Las Heras', operation: 'venta', type: 'casa' },
    questions: [
      ['¿Cómo busco casas en venta en Las Heras?', 'Esta landing carga Mendoza, Las Heras, venta y casa. Podés precisar localidad, precio, superficie, dormitorios y características.'],
      ['¿Qué zonas puedo buscar?', 'Indicá El Challao, Panquehua, El Algarrobal, Uspallata u otra zona para consultar las casas publicadas allí.'],
      ['¿Cómo reviso la ubicación antes de contactar?', 'Consultá el mapa para comparar accesos y distancias, y confirmá dirección, servicios y condiciones directamente con el anunciante.'],
    ],
    related: [
      ['/propiedades/las-heras', 'Propiedades en Las Heras'],
      ['/casas-en-venta/mendoza', 'Casas en venta en Mendoza'],
      ['/alquileres/las-heras', 'Alquileres en Las Heras'],
      ['/casas-en-venta/ciudad-de-mendoza', 'Casas en venta en Ciudad de Mendoza'],
    ],
  },
  '/casas-en-venta/san-luis': {
    title: 'Casas en venta en San Luis | Comprar casa | NIDO',
    description: 'Buscá casas en venta en San Luis. Explorá opciones por ubicación, precio y características desde el listado y el mapa de NIDO.',
    eyebrow: 'COMPRAR CASA EN SAN LUIS',
    heading: 'Casas en venta en San Luis',
    lead: 'Consultá las casas publicadas en venta en San Luis y recorré sus ubicaciones desde el mapa. Podés ajustar precio, superficie y otras características.',
    filters: { province: 'san_luis', operation: 'venta', type: 'casa' },
    related: [
      ['/propiedades/san-luis', 'Todas las propiedades en San Luis'],
      ['/zonas/potrero-de-los-funes', 'Guía de Potrero de los Funes'],
      ['/zonas/el-trapiche-san-luis', 'Guía de El Trapiche'],
      ['/vivir-cuyo/sierras-y-naturaleza', 'Conocer San Luis'],
    ],
  },
};

export default function SearchLanding() {
  const { pathname } = useLocation();
  const landing = LANDINGS[pathname.replace(/\/$/, '')];
  const [filters, setFilters] = useState(() => ({ ...(landing?.filters || {}) }));

  if (!landing) return <NotFound />;

  const handleChange = ({ target }) => {
    const { name, value, type, checked } = target;
    setFilters((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const normalizedPath = pathname.replace(/\/$/, '');
  const [group, slug] = normalizedPath.split('/').filter(Boolean);
  const groupLabel = group === 'alquileres' ? 'Alquileres' : group === 'casas-en-venta' ? 'Casas en venta' : 'Propiedades';
  const breadcrumbItems = [
    { '@type': 'ListItem', position: 1, name: 'NIDO', item: 'https://www.nidoportal.com/' },
    ...(slug !== 'mendoza' && ['propiedades', 'alquileres', 'casas-en-venta'].includes(group)
      ? [{ '@type': 'ListItem', position: 2, name: `${groupLabel} en Mendoza`, item: `https://www.nidoportal.com/${group}/mendoza` }]
      : []),
    { '@type': 'ListItem', position: slug === 'mendoza' ? 2 : 3, name: landing.heading, item: `https://www.nidoportal.com${normalizedPath}` },
  ];
  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id': `https://www.nidoportal.com${normalizedPath}#collection`,
        name: landing.heading,
        description: landing.description,
        url: `https://www.nidoportal.com${normalizedPath}`,
        isPartOf: { '@id': 'https://www.nidoportal.com/#website' },
        breadcrumb: { '@id': `https://www.nidoportal.com${normalizedPath}#breadcrumb` },
        inLanguage: 'es-AR',
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `https://www.nidoportal.com${normalizedPath}#breadcrumb`,
        itemListElement: breadcrumbItems,
      },
      ...(landing.questions?.length ? [{
        '@type': 'FAQPage',
        mainEntity: landing.questions.map(([question, answer]) => ({
          '@type': 'Question',
          name: question,
          acceptedAnswer: { '@type': 'Answer', text: answer },
        })),
      }] : []),
    ],
  };

  return (
    <Page>
      <PageSeo title={landing.title} description={landing.description} path={pathname} structuredData={structuredData} />
      <Hero>
        <HeroCopy>
          <Eyebrow>{landing.eyebrow}</Eyebrow>
          <h1>{landing.heading}</h1>
          <p>{landing.lead}</p>
          <HeroActions>
            <Primary href="#resultados"><FiArrowDown /> Ver propiedades</Primary>
            <Secondary href="#resultados"><FiMap /> Explorar en el mapa</Secondary>
          </HeroActions>
        </HeroCopy>
      </Hero>

      <div id="resultados">
        <PropertyFilters
          filters={filters}
          onChange={handleChange}
          hideOperationTabs={Boolean(landing.filters.operation)}
          eyebrow={landing.eyebrow}
          title="Ajustá tu búsqueda"
        />
        <PropertyList filters={filters} onFilterChange={handleChange} />
      </div>

      {landing.questions?.length > 0 && (
        <Answers aria-labelledby="answers-title">
          <AnswersIntro>
            <Eyebrow>RESPUESTAS PARA TU BÚSQUEDA</Eyebrow>
            <h2 id="answers-title">Lo esencial, antes de elegir.</h2>
            <p>Información directa para aprovechar los filtros, el mapa y las fichas de NIDO.</p>
          </AnswersIntro>
          <AnswerGrid>
            {landing.questions.map(([question, answer], index) => (
              <Answer key={question} open={index === 0}>
                <summary><span>0{index + 1}</span>{question}</summary>
                <p>{answer}</p>
              </Answer>
            ))}
          </AnswerGrid>
        </Answers>
      )}

      <Related aria-labelledby="related-title">
        <RelatedCopy>
          <Eyebrow>SEGUÍ EXPLORANDO</Eyebrow>
          <h2 id="related-title">Búsquedas y guías relacionadas</h2>
          <p>Navegá por otras operaciones y territorios de Cuyo sin perder el contexto de tu búsqueda.</p>
        </RelatedCopy>
        <RelatedLinks>
          {landing.related.map(([to, label]) => <Link key={to} to={to}>{label}<FiArrowRight /></Link>)}
        </RelatedLinks>
      </Related>
    </Page>
  );
}

const Page = styled.div`background:#f7f5ef;color:#29474f;`;
const Hero = styled.header`padding:clamp(70px,9vw,128px) clamp(20px,5vw,72px) clamp(54px,7vw,92px);background:linear-gradient(135deg,#203d44 0%,#315a63 68%,#526f6d 100%);color:#fff;`;
const HeroCopy = styled.div`max-width:1160px;margin:auto;h1{max-width:980px;margin:14px 0 22px;font-size:clamp(46px,7.6vw,106px);font-weight:520;line-height:.9;letter-spacing:-.065em;}p{max-width:760px;margin:0;color:rgba(255,255,255,.78);font-size:clamp(17px,2vw,21px);line-height:1.62;}@media(max-width:600px){h1{font-size:clamp(43px,14vw,64px);line-height:.92;}}`;
const Eyebrow = styled.span`display:block;color:#e09a7c;font-size:12px;font-weight:850;letter-spacing:.18em;`;
const HeroActions = styled.div`display:flex;flex-wrap:wrap;gap:11px;margin-top:30px;@media(max-width:480px){a{width:100%;}}`;
const Primary = styled.a`display:inline-flex;min-height:52px;align-items:center;justify-content:center;gap:9px;padding:0 22px;border-radius:999px;background:#c96f4c;color:#fff;text-decoration:none;font-weight:800;`;
const Secondary = styled(Primary)`border:1px solid rgba(255,255,255,.45);background:transparent;`;
const Answers = styled.section`display:grid;grid-template-columns:minmax(0,.78fr) minmax(0,1.22fr);gap:clamp(38px,8vw,112px);padding:96px max(22px,calc((100vw - 1180px)/2));background:#294f58;color:#fff;@media(max-width:800px){grid-template-columns:1fr;padding-top:70px;padding-bottom:70px;}`;
const AnswersIntro = styled.div`align-self:start;position:sticky;top:28px;h2{max-width:520px;margin:15px 0 18px;font-size:clamp(39px,5vw,68px);font-weight:520;line-height:.94;letter-spacing:-.055em;}p{max-width:480px;margin:0;color:#c8d6d5;line-height:1.7;}@media(max-width:800px){position:static;}`;
const AnswerGrid = styled.div`border-top:1px solid rgba(255,255,255,.18);`;
const Answer = styled.details`border-bottom:1px solid rgba(255,255,255,.18);summary{display:grid;min-height:78px;grid-template-columns:38px minmax(0,1fr);align-items:center;gap:12px;padding:13px 34px 13px 0;color:#fff;font-size:clamp(17px,2vw,21px);font-weight:720;line-height:1.3;cursor:pointer;list-style:none;position:relative;}summary::-webkit-details-marker{display:none;}summary::after{content:'+';position:absolute;right:2px;color:#e09a7c;font-size:25px;font-weight:400;}summary span{color:#e09a7c;font-size:12px;letter-spacing:.12em;}p{margin:0;padding:0 34px 25px 50px;color:#c8d6d5;font-size:15px;line-height:1.75;} &[open] summary::after{content:'−';}@media(max-width:500px){summary{min-height:72px;grid-template-columns:29px minmax(0,1fr);gap:8px;padding-right:28px;font-size:16px;}p{padding-left:37px;padding-right:8px;font-size:14px;}}`;
const Related = styled.section`display:grid;grid-template-columns:minmax(0,.85fr) minmax(0,1.15fr);gap:clamp(36px,8vw,110px);padding:90px max(22px,calc((100vw - 1180px)/2));background:#fff;@media(max-width:760px){grid-template-columns:1fr;padding-top:68px;padding-bottom:68px;}`;
const RelatedCopy = styled.div`h2{margin:13px 0 16px;font-size:clamp(35px,4vw,56px);font-weight:540;line-height:.98;letter-spacing:-.045em;}p{margin:0;color:#6d7d80;line-height:1.7;}`;
const RelatedLinks = styled.nav`display:grid;border-top:1px solid rgba(41,71,79,.16);a{display:flex;min-height:68px;align-items:center;justify-content:space-between;gap:16px;border-bottom:1px solid rgba(41,71,79,.16);color:#29474f;font-size:clamp(17px,2vw,22px);font-weight:700;text-decoration:none;}svg{flex:none;color:#c96f4c;}`;
